import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useMatches
} from '@tanstack/react-router'
import React, { type ReactElement, useContext, useEffect } from 'react'
import { useGetUserToken } from '~/api/mutations/login'
import userDefaultPicture from '~/assets/avatar.svg'
import { ConfirmGlobal } from '~/components/Global/Interactions/Confirm/ConfirmGlobal'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AsideActionWrapper } from '~/components/Layout/AsideActionWrapper/AsideActionWrapper'
import { Footer } from '~/components/Layout/Footer/Footer'
import { Header } from '~/components/Layout/Header/Header'
import { ApplicantModalProvider } from '~/components/Providers/ApplicantModalProvider'
import { AsideActionProvider } from '~/components/Providers/AsideActionProvider'
import { ApplicationModalGlobal } from '~/domains/applications/components/ApplicationModalGlobal'
import { useConfig } from '~/domains/configuration/queries/config'
import { GlobalMessageBanners } from '~/domains/globalMessages/components/GlobalMessageBanners/GlobalMessageBanners'
import { useUserPicture } from '~/domains/users/queries/useUsersPicture'
import {
  globalWrapper,
  headerWrapper,
  mainWrapper,
  menuWrapper,
  pageWrapper
} from '~/pages/styles/Dashboard.css'
import { toast } from '~/utils'
import { AuthContext } from '~/utils/contextes'
import { GlobalSidebar } from './_dashboard/-components/GlobalSidebar'
import { isLegacySettingsPage } from './_dashboard/-components/sidebar'

const DashboardLayout = (): ReactElement | null => {
  const routerMatches = useMatches()
  const location = useLocation()
  const currentUser = useContext(AuthContext)
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  const { data: customerName } = useConfig('instance.customer_name')
  const { data: logoHeader } = useConfig('organization.logo_header')
  const refreshUserToken = useGetUserToken()
  const mainRef = React.useRef<HTMLDivElement>(null)
  const { data: userPicture } = useUserPicture({ userId: currentUser?.id })
  const isSettingsRoute =
    location.pathname.startsWith('/settings') || isLegacySettingsPage
  const currentSection = routerMatches[routerMatches.length - 1]?.fullPath ?? ''

  useEffect(() => {
    let refreshUserTokenInterval: NodeJS.Timeout
    if (currentUser) {
      refreshUserTokenInterval = setInterval(
        () => {
          refreshUserToken.mutate(
            { id: String(currentUser.id) },
            {
              onSuccess: ({ token }) => {
                localStorage.setItem('token', token)
              }
            }
          )
        },
        1000 * 60 * 5
      )
    }
    return () => {
      if (refreshUserTokenInterval) {
        clearInterval(refreshUserTokenInterval)
      }
    }
  }, [currentUser, refreshUserToken])

  if (
    legacyUrl === undefined ||
    customerName === undefined ||
    currentUser == null
  ) {
    return null
  }

  return (
    <AsideActionProvider>
      <ApplicantModalProvider>
        <div className={headerWrapper}>
          <Header
            brand={{
              name: customerName ?? '',
              logo: logoHeader ?? null
            }}
            superUser={currentUser?.isSuperUser}
            user={{
              id: currentUser.id,
              name: `${currentUser.firstname} ${currentUser.lastname}`,
              function: currentUser.function ?? null,
              notificationCount: currentUser.notificationUnreadCount,
              picture: userPicture?.profilePicUrl ?? String(userDefaultPicture)
            }}
            currentSection={currentSection}
            legacyUrl={legacyUrl}
          />
        </div>
        <div className={globalWrapper}>
          <div className={menuWrapper}>
            <GlobalSidebar
              currentSection={currentSection}
              currentPath={routerMatches[routerMatches.length - 1]?.fullPath}
              isSettingsRoute={isSettingsRoute}
              currentUserId={currentUser?.id ?? null}
              legacyUrl={legacyUrl}
              mainRef={mainRef}
            />
          </div>
          <div className={mainWrapper}>
            <main className={pageWrapper} ref={mainRef}>
              <GlobalMessageBanners />
              <Outlet />
            </main>
          </div>
          <AsideActionWrapper />
        </div>
        <Footer />
        <ApplicationModalGlobal />
      </ApplicantModalProvider>
      <ConfirmGlobal />
    </AsideActionProvider>
  )
}

export const Route = createFileRoute('/_dashboard')({
  beforeLoad: ({ context, location }) => {
    if (!context.currentUser) {
      localStorage.removeItem('id')
      localStorage.removeItem('token')
      localStorage.removeItem('instance')
      localStorage.removeItem('email')
      toast('Vous avez été déconnecté', '', ToastType.Danger)

      const redirectUrl = createRedirectUrl(location.href)
      throw redirect({
        to: redirectUrl
      })
    }
  },
  component: DashboardLayout
})

function createRedirectUrl(targetUrl: string): string {
  const url = new URL(targetUrl, window.location.origin)

  if (url.pathname === '/') {
    if (url.searchParams.has('redirect')) {
      const existingRedirect = url.searchParams.get('redirect')
      if (existingRedirect && existingRedirect !== '/') {
        return `/?redirect=${encodeURIComponent(existingRedirect)}`
      }
    }
    return '/'
  }

  return `/?redirect=${encodeURIComponent(targetUrl)}`
}
